import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Icon,
  Loader,
  MessageBanner,
  TrippleToggle,
} from "@src/components/atoms";
import AppUtils from "@src/utils/appUtils";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const SelfReportTable = (props) => {
  const {
    events = [],
    isLoading,
    onEventClick,
    className,
    getDynamicContent,
    industry = "shipping",
    sortedBehaviours,
    onChangeSubmit,
    redirectOnSuccess,
    showPredictions = true,
    setExternalState,
  } = props;

  const [data, _setData] = useState(events);
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobileWidth = useIsMobileWidth();

  useEffect(() => {
    if (!showPredictions || events.length === 0) return;

    const predictedUpdates = [];

    events.forEach((event) => {
      event.behaviours.forEach((bvr) => {
        if (
          !!bvr.isSelfReportable &&
          bvr.succeededPredicted !== null &&
          bvr.succeededPredicted !== undefined
        ) {
          predictedUpdates.push({
            id: bvr.id,
            checked: bvr.succeededPredicted,
            neutral: false,
          });
        }
      });
    });

    setUpdates(predictedUpdates);
    setExternalState && setExternalState(predictedUpdates);
  }, [showPredictions, events]);

  const getToggle = (behaviour, onToggleBvr) => {
    const update = updates?.find((u) => u.id === behaviour.id);

    const neutral = !!update
      ? update?.neutral
      : showPredictions
        ? behaviour.succeededSelfReported === null &&
          behaviour.succeededPredicted === null
        : behaviour.succeededSelfReported === null ||
          behaviour.succeededSelfReported === undefined;

    const checked = !!update
      ? update.checked
      : !!showPredictions
        ? behaviour.succeededSelfReported === true ||
          behaviour.succeededPredicted === true
        : behaviour.succeededSelfReported === true;

    const isToggle =
      !!behaviour.isSelfReportable &&
      behaviour.reasons.some((reason) => reason.includes("FAULTY_DATA"));
    const color = getBehaviourColourForPosition(behaviour.order).name;
    const stateIcon =
      AppUtils.getStateIcon(behaviour.state) || behaviour?._success?.stateIcon;

    const isExclusion =
      behaviour.state === "EXCLUSION" ||
      behaviour?.hasReason ||
      behaviour?._success?.isExclusion;

    return isToggle ? (
      <TrippleToggle
        className="align-middle"
        disabled={behaviour.reasons.length === 0}
        bvrid={behaviour.id}
        backgroundColorLeft="gray"
        backgroundColorCenter="primary"
        backgroundColorRight={color}
        neutral={neutral}
        checked={checked}
        onChange={onToggleBvr}
      />
    ) : (
      <div key={behaviour.id} className="flex-1 self-center text-center">
        <Icon
          key={`${behaviour.id}-${color}`}
          size="8"
          name={stateIcon}
          inline
          backgroundColor={isExclusion ? "neutral-400" : `${color}-500`}
          className="p-1.5"
        />
      </div>
    );
  };

  const getRelevantBehaviours = (events, sortedBehaviours) => {
    const presentAcronyms = new Set();

    events.forEach((event) => {
      event.behaviours.forEach((bvr) => {
        if (bvr.behaviourAcronym) {
          presentAcronyms.add(bvr.behaviourAcronym);
        }
      });
    });

    return sortedBehaviours.filter((acronym) => presentAcronyms.has(acronym));
  };

  const getColumns = (
    isMobileWidth,
    getDynamicContent,
    relBehaviours,
    onEventClick,
    onToggleBvr,
  ) => {
    const noRouteData = events.every(
      (item) => item.origin === "UNKNOWN" && item.destination === "UNKNOWN",
    );
    let aviationColumns = [
      {
        header: (
          <div
            className={`${!noRouteData ? "font-bold hover:text-primary-500 cursor-pointer" : "font-bold"} text-left pl-2`}
          >
            Route
          </div>
        ),
        id: "route",
        accessorFn: (flight) =>
          AppUtils.returnJourneyString(flight.origin, flight.destination),
        cell: (props) => <div className="pl-2 my-4">{props.getValue()}</div>,
        enableSorting: !noRouteData,
      },
      {
        header: (
          <div className="text-left font-bold hover:text-primary-500 cursor-pointer pl-2">
            Date
          </div>
        ),
        id: "departure",
        accessorFn: (flight) =>
          AppUtils.datetimeTinyFormatterUTC(flight.ocurrenceDatetime),
        cell: (props) => <div className="pl-2">{props.getValue()}</div>,
        sortingFn: (a, b) => AppUtils.sortDatetimes(a, b),
        enableSorting: true,
      },
    ];

    let oneColumn = [
      {
        header: (
          <div className="font-bold">
            {getDynamicContent("captain__self_report__table_header_route")}
          </div>
        ),
        id: "event-details",
        accessorFn: (event) => {
          const start = AppUtils.datetimeTinyFormatterUTC(
            event.ocurrenceDatetime,
          );
          const end = AppUtils.datetimeTinyFormatterUTC(event.arrivalDatetime);
          return {
            start,
            end,
            origin: event.origin || "UNKNOWN",
            destination: event.destination || "UNKNOWN",
            id: event.id,
            behaviours: event.behaviours,
          };
        },
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className={"flex flex-col items-center justify-center"}>
              <div
                className={`pt-2 ${onEventClick ? "cursor-pointer hover:text-primary-500" : ""}`}
                onClick={() => {
                  !!onEventClick && onEventClick(value.id);
                }}
              >
                {value.start} - {value.end}
              </div>
              <div
                className={`pb-2 ${onEventClick ? "cursor-pointer hover:text-primary-500" : ""}`}
                onClick={() => {
                  !!onEventClick && onEventClick(value.id);
                }}
              >
                {value.origin} {">"} {value.destination}
              </div>

              {!!isMobileWidth && !!value.behaviours && (
                <div className="pb-2 flex flex-wrap gap-4 justify-center">
                  {[...value.behaviours]
                    .sort(
                      (a, b) =>
                        relBehaviours.indexOf(a.behaviourAcronym) -
                        relBehaviours.indexOf(b.behaviourAcronym),
                    )
                    .map((bvr) => (
                      <div
                        key={bvr.id}
                        className="flex flex-col items-center gap-1"
                      >
                        <div className="font-bold text-sm">
                          {bvr.behaviourAcronym}
                        </div>
                        {getToggle(bvr, onToggleBvr)}
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        },
        sortingFn: (a, b) => AppUtils.sortDatetimes(a, b),
      },
    ];

    const columns = !!isMobileWidth
      ? oneColumn
      : industry === "shipping"
        ? oneColumn
        : aviationColumns;

    if (!isMobileWidth) {
      relBehaviours.forEach((acronym) => {
        columns.push({
          header: <div className="font-bold">{acronym}</div>,
          id: `bvr-${acronym}`,
          accessorFn: (event) => {
            return event.behaviours.find((b) => b.behaviourAcronym === acronym);
          },
          cell: (props) => {
            const bvr = props.getValue();
            return (
              <div className="flex justify-center items-center min-h-[40px]">
                {bvr ? (
                  getToggle(bvr, onToggleBvr)
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
            );
          },
        });
      });
    }

    return columns;
  };

  const updateToggleState = (prevUpdates, bvrId, toggleState) => {
    const existingIndex = prevUpdates.findIndex(
      (update) => update.id === bvrId,
    );

    if (existingIndex !== -1) {
      const newUpdates = [...prevUpdates];
      newUpdates[existingIndex] = { id: bvrId, ...toggleState };
      return newUpdates;
    } else {
      return [...prevUpdates, { id: bvrId, ...toggleState }];
    }
  };
  const onToggleBvr = (bvrId, toggleState) => {
    //update data - for correct table display
    _setData((prevData) =>
      prevData.map((event) => ({
        ...event,
        behaviours: event.behaviours.map((bvr) =>
          bvr.id === bvrId
            ? {
                ...bvr,
                succeededSelfReported: toggleState.neutral
                  ? null
                  : toggleState.checked,
              }
            : bvr,
        ),
      })),
    );

    setUpdates((prev) => updateToggleState(prev, bvrId, toggleState));
    if (setExternalState) {
      setExternalState((prev) => updateToggleState(prev, bvrId, toggleState));
    }
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      const dataToSave = updates
        .map((u) => {
          if (!u.neutral) return { id: u.id, selfReported: u.checked };
        })
        .filter((u) => !!u);
      await onChangeSubmit(dataToSave);
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit comment. Please try again.");
      console.error("Submission error:", err);
    } finally {
      setIsSubmitting(false);
      redirectOnSuccess && redirectOnSuccess();
    }
  };

  const relevantBehaviours = getRelevantBehaviours(events, sortedBehaviours);
  const table = useReactTable({
    data,
    columns: getColumns(
      isMobileWidth,
      getDynamicContent,
      relevantBehaviours,
      onEventClick,
      onToggleBvr,
    ),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className={className}>
        {isLoading && <Loader size="2xl" />}

        {!isLoading && !!table && (
          <div className="p-2">
            <table className="w-full divide-y divide-gray-200 ">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-gray-500">
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {data.length > 0 ? (
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-200">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllLeafColumns().length}
                    className="text-center py-6 text-gray-500"
                  >
                    Great! You're all caught up! There are no events to
                    self-report.
                  </td>
                </tr>
              )}
              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </table>
            <div className="h-4" />
          </div>
        )}
      </div>
      {!submitted && (
        <div className="flex flex-col text-sm text-gray-500 p-2 text-center">
          <div>
            By submitting this form, I accept that these reports are true to the
            best of my knowledge and memory.
          </div>
          <Button onClick={onSubmit} loading={isSubmitting}>
            Save self-reported results
          </Button>
        </div>
      )}
      {submitted && error === "" && (
        <div className="mt-4">Self-reported results saved successfully.</div>
      )}
      {error !== "" && <div className="mt-4">{error}</div>}
    </>
  );
};

export const selfReportTableControls = {
  events: {
    propType: PropTypes.array,
    description: "Array of events",
    controlType: "object",
    defaultValue: [],
  },
  getDynamicContent: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
  isLoading: COMMON_CONTROLS.isLoading,
  onEventClick: {
    propType: PropTypes.func,
    description: "Event click handler",
    disable: true,
  },
  onChangeSubmit: {
    propType: PropTypes.func.isRequired,
    description: "Event click handler",
    disable: true,
  },
  className: COMMON_CONTROLS.className,
};

SelfReportTable.propTypes = AppUtils.getPropTypeFromControls(
  selfReportTableControls,
);

export default SelfReportTable;
